import { css, SerializedStyles } from '@emotion/core'
import React from 'react'
import { createPortal } from 'react-dom'

import { breakpoints } from '../constants/breakPoints'
import { longCopyMobileBreakPoint } from '../constants/breakPoints'
import { isMobile } from '../helpers/common'
import AnchorsMobileNavigation from './AnchorsMobileNavigation'

export interface AnchorProps {
  id: string
  label: string
  mobileLabel?: string
  hash?: number | string
  subAnchors?: { id: string; label?: string }[]
  index?: number
}

export interface AnchorComponentProps {
  anchors: AnchorProps[]
  currentId?: string
  cssProp?: SerializedStyles
  title?: string
  subtitle?: string
  blackBG?: boolean
  withoutMobileNavigation?: boolean
}

const moveNavigationCursor = (currentId: string) => {
  // timeout: wait for new subAnchors to be rendered
  // then scroll in updated anchorList
  const timeoutId = setTimeout(() => {
    const element = document.getElementById(`anchor-item-${currentId}`)

    if (element) {
      const rect = element.getBoundingClientRect()
      const isVisible = rect.top <= window.innerHeight && rect.bottom > window.innerHeight

      if (isMobile(longCopyMobileBreakPoint)) {
        // title ("CONTENTS") is sticky
        document
          .getElementById('anchors-mobile-container')!
          .scrollTo({ top: element.offsetTop - 72, behavior: 'smooth' })
      } else if (!isVisible) {
        document.getElementById('anchors-container')!.scrollTo({ top: element.offsetTop - 42, behavior: 'smooth' })
      }
    }
  }, 500)

  return () => clearTimeout(timeoutId)
}

const Anchors: React.FC<AnchorComponentProps> = ({
  anchors,
  currentId,
  cssProp,
  title = 'CONTENTS',
  blackBG = false,
  withoutMobileNavigation = false,
}) => {
  if (anchors.length === 0) {
    return null
  }

  const [currentAnchor, setCurrentAnchor] = React.useState<string>(currentId || anchors[0].id)
  const [currentSubAnchor, setCurrentSubAnchor] = React.useState<string>()
  const [scrolling, setScrolling] = React.useState(!!currentId)

  React.useEffect(() => {
    if (!!currentId) {
      if (anchors[0].id !== currentId) {
        document.getElementById(currentId)?.scrollIntoView()
      }
      setTimeout(
        () => {
          setScrolling(false)
        },
        (anchors.length + 1) * 100
      )

      setTimeout(() => moveNavigationCursor(currentId), 1200)
    }
  }, [])

  React.useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '20% 0% -60%',
      threshold: 0.1,
    }

    const observer = new IntersectionObserver((records) => {
      if (records[0].isIntersecting && !scrolling) {
        const relatedAnchor =
          anchors.find(({ id }) => id === records[0].target.id) ||
          anchors.filter(({ subAnchors }) => subAnchors?.find(({ id }) => id === records[0].target.id))[0]

        const relatedSubAnchor = anchors.flatMap((anchor) =>
          anchor.subAnchors
            ? anchor.subAnchors.map((subAnchor) => subAnchor.id).filter((id) => id === records[0].target.id)
            : []
        )

        setCurrentAnchor(relatedAnchor.id)
        // if subAnchor is returned in array: set it as currentSubAnchor
        // if not: reset currentSubAnchor (currentAnchor is the capital anchor)
        setCurrentSubAnchor(relatedSubAnchor.length > 0 ? relatedSubAnchor[0] : '')
        moveNavigationCursor(relatedAnchor.id)
        if (relatedAnchor.hash && typeof window !== 'undefined') {
          window.location.hash = `#${relatedAnchor.hash}/`
        }
      }
    }, observerOptions)

    if (!scrolling) {
      anchors.forEach(({ id, subAnchors }) => {
        const observeTarget = document.getElementById(id)
        if (observeTarget) {
          observer.observe(observeTarget)
        }

        subAnchors?.forEach(({ id }) => {
          const subAnchorsObserveTarget = document.getElementById(id)
          if (subAnchorsObserveTarget) {
            observer.observe(subAnchorsObserveTarget)
          }
        })
      })
    }

    return () => observer.disconnect()
  }, [anchors, scrolling])

  const onClickHandlerAnchors = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, anchor: AnchorProps) => {
    e.preventDefault()
    setScrolling(true)
    setCurrentAnchor(anchor.id)

    setTimeout(() => {
      setScrolling(false)
    }, 1500)

    const elmnt = document.getElementById(anchor.id)
    elmnt?.scrollIntoView()

    setTimeout(() => {
      if (anchor.hash && typeof window !== 'undefined') {
        window.location.hash = `#${anchor.hash}/`
      }
    }, 1000)
  }

  const onClickHandlerSubAnchors = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, subAnchorId: string) => {
    e.preventDefault()
    setScrolling(true)
    setCurrentSubAnchor(subAnchorId)

    setTimeout(() => {
      setScrolling(false)
    }, 1500)

    const elmnt = document.getElementById(subAnchorId)
    elmnt?.scrollIntoView()
  }

  // mobile anchors
  if (isMobile(longCopyMobileBreakPoint) && !withoutMobileNavigation) {
    return (
      <>
        {createPortal(
          <AnchorsMobileNavigation
            anchors={anchors}
            title={title}
            currentAnchor={currentAnchor}
            currentSubAnchor={currentSubAnchor}
            setCurrentSubAnchor={setCurrentSubAnchor}
            onClickHandlerAnchors={onClickHandlerAnchors}
            onClickHandlerSubAnchors={onClickHandlerSubAnchors}
            currentId={currentAnchor}
            blackBG={blackBG}
          />,
          document.body
        )}
      </>
    )
  }

  // desktop anchors
  return (
    <div css={[anchorsContainerStyle, cssProp]} id="anchors-container" className="hide-on-small-medium">
      <div>
        <div css={anchorsTitleStyle(blackBG)} className="strong-body-1">
          {title}
        </div>
        <ul>
          {anchors.map((anchor, index) => {
            const isCurrentAnchor = currentAnchor === anchor.id
            return (
              <li key={`anchor-item-${index}`} id={`anchor-item-${anchor.id}`}>
                <div key={anchor.id} css={linkContainerStyle}>
                  <a
                    css={linkStyle}
                    className={`body-2 opacity-on-hover ${
                      isCurrentAnchor ? 'blue-text bold' : blackBG ? 'white-text' : 'black-text'
                    }`}
                    href={`#${anchor.id}/`}
                    onClick={(e) => onClickHandlerAnchors(e, anchor)}
                  >
                    {anchor.label}
                  </a>
                </div>
                {anchor.id === currentAnchor && anchor.subAnchors && (
                  <ul>
                    {anchor.subAnchors.map((subAnchor) => {
                      const isCurrentSubAnchor = currentSubAnchor === subAnchor.id
                      return (
                        <li key={subAnchor.id}>
                          <a
                            className={`body-2 ${isCurrentSubAnchor ? 'blue-text' : 'black-text'} opacity-on-hover`}
                            css={[subAnchorLinkStyle(currentSubAnchor === subAnchor.id)]}
                            onClick={(e) => onClickHandlerSubAnchors(e, subAnchor.id)}
                          >
                            <div>{subAnchor.label}</div>
                          </a>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export default Anchors

const anchorsContainerStyle = css`
  display: flex;
  flex-direction: column;
  max-width: 17rem;
  position: sticky;
  top: 5.5rem;
  overflow-y: scroll;
  max-height: calc(100vh - 7rem);
  --ms-overflow-style: none;
  scrollbar-width: none;
  margin-right: 10%;

  &::-webkit-scrollbar {
    display: none;
  }

  /* for firefox */
  scrollbar-width: none;

  ul {
    li {
      margin-bottom: 0.75rem;
    }
    ul {
      margin-left: 1rem;
      margin-top: 0.75rem;
    }
  }
`

const anchorsTitleStyle = (blackBG: boolean) => css`
  // richt-text (center column) always has a slight margin-top for which we need to align
  margin-top: 0.25rem;
  z-index: 2;
  position: sticky;
  top: 0;
  background: linear-gradient(
    180deg,
    rgba(${blackBG ? '7, 19, 34' : '255, 255, 255'}, 1),
    rgba(${blackBG ? '7, 19, 34, 0.98' : '255, 255, 255, 0.95'}) 70%,
    rgba(${blackBG ? '7, 19, 34, 0.90' : '255, 255, 255, 0.80'}) 90%,
    rgba(${blackBG ? '7, 19, 34' : '255, 255, 255'}, 0.1) 100%
  );
  padding-bottom: 1rem;
`

const linkStyle = css`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 0 0.75rem !important;
`

const linkContainerStyle = css`
  margin-bottom: 0.75rem;
  @media screen and (min-width: ${breakpoints.xxlarge}px) {
    margin-bottom: 1.25rem;
  }
`

// export for <AnchorsMobileNavigation />
export const subAnchorLinkStyle = (isCurrentAnchor?: boolean) => css`
  text-overflow: ellipsis;
  ${isCurrentAnchor && `color: #318eff !important;`};
`
